import React, { useEffect, useRef } from "react";
import { MapContainer, TileLayer, Marker, useMap, useMapEvents } from "react-leaflet";
import { Button, message } from "antd";
import { AimOutlined } from "@ant-design/icons";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import icon from "leaflet/dist/images/marker-icon.png";
import iconShadow from "leaflet/dist/images/marker-shadow.png";

// CRA doesn't resolve Leaflet's default marker icon URLs correctly out of
// the box — re-point them at the bundled assets, same fix needed in every
// CRA + react-leaflet setup.
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: icon,
  iconUrl: icon,
  shadowUrl: iconShadow,
});

const ClickHandler = ({ onPick }) => {
  useMapEvents({
    click(e) {
      onPick(e.latlng.lat, e.latlng.lng);
    },
  });
  return null;
};

/**
 * Leaflet measures its container's size once, at mount. When the map mounts
 * while its parent (an antd Modal) is still mid open-animation, it measures
 * a container that hasn't reached its final size yet — Leaflet then only
 * ever renders/positions tiles for that too-small area (tiles pile up in
 * one corner, the rest stays blank grey), exactly the bug reported here.
 * `invalidateSize()` after the modal's transition finishes fixes it.
 */
const MapController = ({ lat, lng }) => {
  const map = useMap();
  const hasCenteredRef = useRef(false);

  useEffect(() => {
    const timers = [100, 350, 600].map((ms) => setTimeout(() => map.invalidateSize(), ms));
    const onResize = () => map.invalidateSize();
    window.addEventListener("resize", onResize);
    return () => {
      timers.forEach(clearTimeout);
      window.removeEventListener("resize", onResize);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (lat == null || lng == null) return;
    // Recenter on lat/lng changes (typed manually, or from "Use my location")
    // without remounting the whole map — remounting on every change was the
    // other half of this bug (it re-triggers the same size-measurement race
    // on every click/keystroke).
    if (!hasCenteredRef.current) {
      hasCenteredRef.current = true;
      return;
    }
    map.setView([lat, lng], map.getZoom());
  }, [lat, lng, map]);

  return null;
};

// No API key needed: OpenStreetMap tiles + Leaflet, per plan's geofencing
// decision. Click anywhere on the map to set lat/lng, or use "Use my
// location" to read the browser's own Geolocation API.
const OfficeLocationPicker = ({ lat, lng, onChange, height = 300 }) => {
  const center = [lat || 28.6139, lng || 77.209]; // default: New Delhi

  const useMyLocation = () => {
    if (!navigator.geolocation) {
      message.error("Geolocation is not supported by this browser");
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => onChange(pos.coords.latitude, pos.coords.longitude),
      (err) => message.error(err.message || "Could not get your location"),
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  return (
    <div style={{ position: "relative" }}>
      <MapContainer center={center} zoom={15} style={{ height, width: "100%" }}>
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        {lat != null && lng != null && <Marker position={[lat, lng]} />}
        <ClickHandler onPick={onChange} />
        <MapController lat={lat} lng={lng} />
      </MapContainer>
      <Button
        size="small"
        icon={<AimOutlined />}
        onClick={useMyLocation}
        style={{ position: "absolute", top: 10, right: 10, zIndex: 1000, boxShadow: "var(--i-ems-shadow-sm)" }}
      >
        Use my location
      </Button>
    </div>
  );
};

export default OfficeLocationPicker;
